

import { ArrowRight, ChevronDown, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'src/components/ui/dropdown-menu';
import DateRangeModal from '../../../performance/components/date-range-modal';
import { useGetAccountsByPlatformQuery, useGetCampaignsByAccountQuery, useGetLineItemsByCampaignAndAccountQuery, useGetPlatformsQuery } from 'src/redux/admin/reporting/reporting.endpoints';
import MultiSelector from './multi-selector';
import { DateFilterType } from 'src/pages/performance/tabs/overview-tab';
import { formatDateForInputLocal } from 'src/helpers/date.helpers';
import { formatPlatformLabel } from 'src/pages/reports/utils/format-reporting-labels';

export interface ReportMenuBarSelection {
  source: string;
  accounts: {id: string | number, name: string}[];
  campaigns: {id: string | number, name: string}[];
  lineItems: {id: string | number, name: string}[];
  dateFilter: string;
  startDate: string;
  endDate: string;
}

interface ReportsMenuBarProps {
  className?: string;
  selectedClassName?: string;
  onEditToggle: () => void;
  onSelectionChange: (selection: ReportMenuBarSelection) => void;
  onDateChange: (dateFilter: string, startDate?: string, endDate?: string) => void;
  editMode?: boolean;
  selectedMetric?: string;
  selectedDate?: string;
}

const ReportsMenuBar: React.FC<ReportsMenuBarProps> = ({
  className = 'bg-gray-200 text-black rounded-full',
  selectedClassName = 'bg-blue-500 text-white',
  onEditToggle,
  onSelectionChange,
  onDateChange,
  editMode = false,
  selectedMetric = '',
  selectedDate = '',
}) => {
  const urlParams = new URLSearchParams(location.search);
  const [selectedPlatform, setSelectedPlatform] = useState<string>(urlParams.get("platform") || "");
  // Keep raw string IDs — OpenAI campaign/ad-group IDs are non-numeric (e.g. cmpn_…).
  const [selectedAccounts, setSelectedAccounts] = useState<{id: string | number, name: string}[]>(
    urlParams.get("accountIds")
      ? urlParams.get("accountIds")!.split(',').filter(Boolean).map(id => ({ id, name: '' }))
      : [],
  );
  const [selectedCampaigns, setSelectedCampaigns] = useState<{id: string | number, name: string}[]>(
    urlParams.get("campaignIds")
      ? urlParams.get("campaignIds")!.split(',').filter(Boolean).map(id => ({ id, name: '' }))
      : [],
  );
  const [selectedLineItems, setSelectedLineItems] = useState<{id: string | number, name: string}[]>(
    urlParams.get("lineItemIds")
      ? urlParams.get("lineItemIds")!.split(',').filter(Boolean).map(id => ({ id, name: '' }))
      : [],
  );
  const [dateFilter, setDateFilter] = useState<DateFilterType>(selectedDate as DateFilterType);
  const [startDate, setStartDate] = useState<string>(urlParams.get("startDate") || "");
  const [endDate, setEndDate] = useState<string>(urlParams.get("endDate") || "");

  const rawPreviewClientId = urlParams.get('previewClientId');
  const previewClientId =
    rawPreviewClientId && rawPreviewClientId !== 'null' && rawPreviewClientId !== 'undefined'
      ? rawPreviewClientId
      : undefined;

  const { data: platforms = [], isFetching: isFetchingPlatforms } = useGetPlatformsQuery({ previewClientId });

  const { data: accounts = [], isFetching: isFetchingAccounts, error: accountsError } = useGetAccountsByPlatformQuery(
    { source: selectedPlatform, previewClientId }, { skip: !selectedPlatform });

  const { data: campaigns = [], isFetching: isFetchingCampaigns, error: campaignsError, refetch: refetchCampaigns } = useGetCampaignsByAccountQuery(
    { source: selectedPlatform, accountId: selectedAccounts.map((account) => account.id), previewClientId }, { skip: selectedAccounts.length === 0 });

  const { data: lineItems = [], isFetching: isFetchingLineItems, error: lineItemsError } = useGetLineItemsByCampaignAndAccountQuery(
    { source: selectedPlatform, accountId: selectedAccounts.map((account) => account.id), campaignId: selectedCampaigns.map((campaign) => campaign.id), previewClientId }, { skip: selectedAccounts.length === 0 || selectedCampaigns.length === 0 });

  useEffect(() => {
    if (platforms.length > 0 && (selectedPlatform === '' || selectedPlatform === null || selectedPlatform === undefined)) {
      setSelectedPlatform(platforms[0]);
    }
  }, [platforms]);

  useEffect(() => {
    setSelectedCampaigns([]);
    if (selectedAccounts.length > 0) {
      refetchCampaigns();
    }
  }, [selectedAccounts, refetchCampaigns]);


  const handlePlatformSelection = (platform: string) => {
    setSelectedPlatform(platform);
    setSelectedAccounts([]);
    setSelectedCampaigns([]);
    setSelectedLineItems([]);
  };

  const handleAccountSelection = (items: {id: string | number, name: string}[]) => {
    setSelectedAccounts(items);
    setSelectedCampaigns([]);
    setSelectedLineItems([]);
  };

  const handleCampaignSelection = (items: {id: string | number, name: string}[]) => {
    setSelectedCampaigns(items);
    setSelectedLineItems([]);
  };

  const handleLineItemSelection = (items: {id: string | number, name: string}[]) => {
    setSelectedLineItems(items);
  };

  const handleDateChange = (dateFilter: string, _startDate?: string, _endDate?: string) => {
    setDateFilter(dateFilter as DateFilterType);

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDate = now.getDate();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    let newStartDate = '';
    let newEndDate = '';

    switch (dateFilter) {
      case 'last_month':
        newStartDate = formatDateForInputLocal(new Date(currentYear, currentMonth - 1, 1));
        newEndDate = formatDateForInputLocal(new Date(currentYear, currentMonth, 0));
        break;

      case 'this_month':
        newStartDate = formatDateForInputLocal(new Date(currentYear, currentMonth, 1));
        newEndDate = formatDateForInputLocal(yesterday);
        break;

      case 'last_week':
        newStartDate = formatDateForInputLocal(new Date(currentYear, currentMonth, currentDate - 8));
        newEndDate = formatDateForInputLocal(yesterday);
        break;

      case 'last_quarter':
        newStartDate = formatDateForInputLocal(new Date(currentYear, currentMonth - 3, 1));
        newEndDate = formatDateForInputLocal(yesterday);
        break;

      case 'last_3_days':
        newStartDate = formatDateForInputLocal(new Date(currentYear, currentMonth, currentDate - 4));
        newEndDate = formatDateForInputLocal(yesterday);
        break;

      case 'yesterday':
        newStartDate = formatDateForInputLocal(new Date(currentYear, currentMonth, currentDate - 2));
        newEndDate = formatDateForInputLocal(yesterday);
        break;

      case 'year_to_date':
        newStartDate = formatDateForInputLocal(new Date(currentYear, 0, 1));
        newEndDate = formatDateForInputLocal(yesterday);
        break;

      case 'custom':
        if (_startDate && _endDate) {
          newStartDate = _startDate;
          newEndDate = _endDate;
        }
        break;

      default:
        newStartDate = _startDate || formatDateForInputLocal(new Date(currentYear, currentMonth - 1, 1));
        newEndDate = _endDate || formatDateForInputLocal(new Date(currentYear, currentMonth, 0));
        break;
    }

    // Set the state
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleClear = () => {
    handlePlatformSelection(platforms[0]);
    setStartDate('');
    setEndDate('');
    setDateFilter('' as DateFilterType);
  };

  useEffect(() => {
    onSelectionChange({
      source: selectedPlatform,
      accounts: selectedAccounts,
      campaigns: selectedCampaigns,
      lineItems: selectedLineItems,
      dateFilter: dateFilter,
      startDate: startDate,
      endDate: endDate,
    });
  }, [selectedPlatform, selectedAccounts, selectedCampaigns, selectedLineItems, dateFilter, startDate, endDate]);

  return (
    <div className="flex justify-between w-full flex-col gap-2">
      <div className="flex items-center gap-2">
        <div id="platform-dropdown">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className={`ml-2 ${className} hover:opacity-65`}
                variant="ghost"
                size="med"
              >
                <ChevronDown className="w-4 h-4" />
                {formatPlatformLabel(selectedPlatform)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {platforms.map((platform) => (
                <DropdownMenuItem
                  key={platform}
                  onClick={() => handlePlatformSelection(platform)}
                >
                  {formatPlatformLabel(platform)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div id="account-dropdown">
          {selectedPlatform && (
            <MultiSelector
              isLoading={isFetchingAccounts}
              selected={selectedAccounts}
              items={accounts.map(account => ({ id: account.id, name: account.name }))}
              onSelectionChange={(items) => handleAccountSelection(items)}
              placeholder="Select Accounts"
              label="Accounts"
            />
          )}

        </div>
        <div id="campaign-dropdown">
          {selectedPlatform && selectedAccounts.length > 0 && (
            <MultiSelector
              isLoading={isFetchingCampaigns}
              selected={selectedCampaigns}
              items={campaigns.map(campaign => ({ id: campaign.id, name: campaign.name }))}
              onSelectionChange={(items) => handleCampaignSelection(items)}
              placeholder="Select Campaigns"
              label="Campaigns"
            />
          )}

        </div>
        <div id="line-item-dropdown">
          {selectedPlatform && selectedAccounts.length > 0 && selectedCampaigns.length > 0 && (
            <MultiSelector
              selected={selectedLineItems}
              isLoading={isFetchingLineItems}
              items={lineItems.map(lineItem => ({ id: lineItem.id, name: lineItem.name }))}
              onSelectionChange={(items) => handleLineItemSelection(items)}
              placeholder="Select Line Items"
              label="Line Items"
            />
          )}
        </div>
        <Button
          className='ml-auto bg-gray-201'
          variant="ghost"
          size="med"
          onClick={() => handleClear()}
        >
          <X className='w-4 h-4' />
          Clear
        </Button>

      </div>
      <div className='flex items-center gap-4 border-b border-gray-201' >

        {/* <div className='text-xs font-normal text-gray-500'>
          <span className='font-medium'>
            Source:
          </span>
          {' '}
          {selectedPlatform.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
        </div>
        <div className='text-xs font-normal text-gray-500 max-w-[1/4]'>
          <span className='font-medium'>
            Accounts:
          </span>
          {' '}
          {selectedAccounts.map(account => account.name).join(', ')}
        </div> */}

      </div>
      <div className='flex items-center gap-2 justify-end'>
        {platforms && selectedAccounts.length > 0 && (startDate === '' || endDate === '') && (
          <div className='flex items-center gap-2 '>
            {/* <span className='text-xs font-normal text-gray-500'>
              Select a date
            </span> */}
            <ArrowRight className='w-6 h-6 animate-bounce' />
          </div>
        )}
        <Button
          className={`${className} hover:opacity-65 ${dateFilter === 'last_week' ? selectedClassName : ''}`}
          variant="primary"
          size="med"
          onClick={() => handleDateChange('last_week')}
        >
          Last week
        </Button>

        <Button
          className={`${className} hover:opacity-65 ${dateFilter === 'last_month' ? selectedClassName : ''}`}
          variant="ghost"
          size="med"
          onClick={() => handleDateChange('last_month')}
        >
          Last month
        </Button>

        <Button
          className={`${className} hover:opacity-65 ${dateFilter === 'this_month' ? selectedClassName : ''}`}
          variant="ghost"
          size="med"
          onClick={() => handleDateChange('this_month')}
        >
          This month
        </Button>

        <DateRangeModal
          className={className}
          selectedClassName={selectedClassName}
          selectedDate={dateFilter}
          onDateChange={handleDateChange}
        />

      </div>
    </div>
  );
};

export default ReportsMenuBar;
